<template>
    <Dialog
        ref="dialogRef"
        v-model:visible="widgetModalState.opened"
        class="generated-link-modal"
        :class="minimized
            ? 'minimized w-full h-auto'
            : 'w-10/12 h-5/6'"
        :style="dialogStyle"
        append-to="#app"
        maximizable
        draggable
        :modal="!minimized"
        :dismissable-mask="false"
        :content-class="minimized ? '!h-0 !p-0' : 'grow'"
        :pt="{ header: { class: '!justify-end !py-2 !px-4' } }"
        data-testid="tl-dialog"
        @maximize="maximize"
        @unmaximize="unmaximize"
        @update:visible="handleVisibilityChange"
    >
        <template #header>
            <div class="w-full flex gap-4 pl-2">
                <div class="font-semibold">{{ widgetModalState.workflowName || 'Workflow' }}</div>
                <div
                    v-if="widgetModalState.recordId"
                    class="text-sm"
                >record ID: {{ widgetModalState.recordId }}</div>
            </div>
            <Button
                v-if="!dialogRef.maximized"
                id="minimize-trigger"
                type="reset"
                severity="secondary"
                text
                class="mr-[var(--p-dialog-header-gap)]"
                :icon="minimized
                    ? 'pi pi-arrow-up-right-and-arrow-down-left-from-center'
                    : 'pi pi-arrow-down-left-and-arrow-up-right-to-center'"
                :aria-label="'Close'"
                data-testid="minimize-modal"
                @click="minimizeToggle"
            />
        </template>

        <div
            v-show="!minimized"
            class="widget-modal-wrapper"
        >
            <transactionlink-widget />
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import Dialog from 'primevue/dialog'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import useWidgetScript from '@/composables/widget/useWidgetScript'
import useWidgetModal from '@/composables/widget/useWidgetModal'
import { useEventBus } from '@vueuse/core'
import { useLockedRecords } from '@/modules/custom-table/composables/useLockedRecords'

const { widgetModalState, closeWidgetModal } = useWidgetModal()

const {
    unlockRecord,
    handleLockedRecordError,
} = useLockedRecords()

const widgetChangeIndicator = ref(false)

const hideModal = () => {
    closeWidgetModal()
}

const { load, unload } = useWidgetScript(
    (api) => {
        api.setOptions({
            token: widgetModalState.value.token,
            inline: true,
        })
        api.open()

        api.on('workflow-finished', () => {
            unlockRecord(widgetModalState.value.recordId)
            hideModal()
            useEventBus('widget-modal-workflow-finished').emit()
        })

        api.on('view-context-changed', () => {
            useEventBus('widget-modal-view-context-changed').emit()

            widgetChangeIndicator.value = true
            setTimeout(() => {
                widgetChangeIndicator.value = false
            }, 1000)
        })

        api.on('error', (error) => {
            handleLockedRecordError(widgetModalState.value.recordId, error.message || 'Unknown error')
            hideModal()
        })
    },
)

const dialogRef = ref<ANY>()

const minimized = ref(false)
const wasMinimized = ref(false)

const dialogStyle = computed(() => {
    const transition = 'border 0.3s ease-in-out'

    if (!minimized.value) return { transition }

    const border = widgetChangeIndicator.value
        ? '2px solid var(--color-tl-blue)'
        : '1px solid var(--color-black-tint-heavy)'

    return {
        transition,
        border,
    }
})

const maximize = () => {
    wasMinimized.value = minimized.value
    minimized.value = false
}

const unmaximize = () => {
    minimized.value = wasMinimized.value
}

const getDialogElement = () => {
    return document.querySelector('.generated-link-modal') as HTMLElement | undefined
}

const centerDialogWindow = () => {
    const dialogEl = getDialogElement()
    if (!dialogEl) return

    const rect = dialogEl.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const newLeft = (viewportWidth - rect.width) / 2
    const newTop = (viewportHeight - rect.height) / 2

    dialogEl.style.left = `${newLeft}px`
    dialogEl.style.top = `${newTop}px`

    document.body.classList.add('p-overflow-hidden')
}

const setMinimizedStyles = () => {
    const dialogEl = getDialogElement()
    if (!dialogEl) return

    dialogEl.style.position = 'fixed'
    dialogEl.style.top = `${window.innerHeight - 56}px`
    dialogEl.style.left = '0px'
    document.body.classList.remove('p-overflow-hidden')
}

const minimizeToggle = () => {
    if (dialogRef.value?.maximized) {
        dialogRef.value?.maximize()
    }

    minimized.value = !minimized.value

    nextTick(() => {
        if (minimized.value) {
            setMinimizedStyles()
        } else {
            centerDialogWindow()
        }
    })
}

const handleVisibilityChange = (isOpened: boolean) => {
    if (!isOpened) {
        unlockRecord(widgetModalState.value.recordId)
        closeWidgetModal()
    }
}

onMounted(() => {
    load()
})

onUnmounted(() => {
    unload()
})
</script>

<style lang="sass" scoped>

.widget-modal-wrapper
    width: 100%
    height: 100%
</style>
